/**
 * Boats, the player's side: placing a boat item on water / a block, climbing
 * in (right click), paddling (W/S/A/D via `Player.rideInput`), Shift to climb
 * out, punching a boat to pieces, and keeping the rider seated after each
 * physics tick. Sits on the shared mount model (game/riding.ts) next to the
 * mob mounts (game/mounts.ts); the physics lives in entities/vehicles/boat.ts.
 *
 * Wiring (main.ts):
 *  - `boats = createBoats(deps)` once, `boats.sim` is what the renderer draws;
 *  - right click with a boat item -> `boats.place(held.id, eye, dir)`;
 *  - right click on a boat        -> `boats.use(eye, dir, blockDist, sneaking)`;
 *  - left click                   -> `boats.punch(eye, dir, blockDist)`;
 *  - after entities.tick()        -> `boats.tick(stop)`.
 * DOM-free: the Player is only touched through the small `BoatPlayer` shape.
 */

import { IS_TARGETABLE, IS_WATERY } from '../blocks';
import { waterHeight } from '../world/fluid';
import { BoatSim, BOAT_ITEMS, boatWoodOf, type Boat, type BlockReader } from '../entities/vehicles/boat';
import { mountPlayer, dismountPlayer, ride, PLAYER, type RiderInput } from './riding';

/** Ray hit in block space (same shape as player.ts RayHit). */
export interface BoatRayHit { x: number; y: number; z: number; id: number; nx: number; ny: number; nz: number }

/** The slice of the Player a boat rider needs. */
export interface BoatPlayer {
  yaw: number;
  sitting: boolean;
  rideTick: (() => void) | null;
  rideInput(out: { fwd: number; strafe: number; jump: boolean; jumpTapped: boolean; sneak: boolean }): void;
  rideTo(x: number, y: number, z: number, snap?: boolean): void;
  setBodyYaw(y: number): void;
  resetFall(): void;
}

export interface BoatDeps {
  player: BoatPlayer;
  read: BlockReader;
  loaded?: (x: number, z: number) => boolean;
  /** Raycast the world; `targets` additionally lets the ray stop at water. */
  ray: (targets: Uint8Array) => BoatRayHit | null;
  creative: () => boolean;
  /** Hit points of the held item's melee attack (a fist is 1). */
  punchDamage: () => number;
  drop: (item: number, x: number, y: number, z: number) => void;
  message?: (msg: string) => void;
}

/** What a boat-placing ray stops at: every targetable block and any water. */
const BOAT_TARGETS = IS_TARGETABLE.slice();
for (let i = 0; i < BOAT_TARGETS.length; i++) if (IS_WATERY[i] === 1) BOAT_TARGETS[i] = 1;

/** Distance along the ray (unit `d`) where it enters the unit cell (cx, cy, cz); 0 when already inside. */
function cellEntry(o: readonly number[], d: readonly number[], c: readonly number[]): number {
  let t0 = 0, t1 = 1e9;
  for (let a = 0; a < 3; a++) {
    if (Math.abs(d[a]) < 1e-9) continue;
    let ta = (c[a] - o[a]) / d[a], tb = (c[a] + 1 - o[a]) / d[a];
    if (ta > tb) { const t = ta; ta = tb; tb = t; }
    t0 = Math.max(t0, ta); t1 = Math.min(t1, tb);
  }
  return t0 <= t1 ? t0 : 0;
}

export function createBoats(deps: BoatDeps) {
  const sim = new BoatSim(deps.read, deps.loaded);
  const p = deps.player;
  const input: RiderInput & { jumpTapped: boolean; fwd: number } = { forward: 0, strafe: 0, jump: false, sneak: false, jumpTapped: false, fwd: 0 };
  const tmp = { x: 0, y: 0, z: 0 };
  let prevHook: (() => void) | null = null;
  let age = 0;

  const riding = (): Boat | null => (ride.current && (ride.current as Boat).kind === 'boat' && sim.boats.includes(ride.current as Boat) ? (ride.current as Boat) : null);

  function syncRider(snap = false): void {
    const b = riding();
    if (!b) return;
    b.seatPos(ride.seat, tmp);
    p.rideTo(tmp.x, tmp.y, tmp.z, snap);
    p.setBodyYaw(b.yaw);
  }

  /** The player climbs out at the nearest free spot. */
  function dismount(): void {
    const b = riding();
    const v = dismountPlayer();
    p.sitting = false;
    p.rideTick = prevHook; prevHook = null;
    b?.clearInput();
    if (v) { v.dismountPos(tmp); p.rideTo(tmp.x, tmp.y, tmp.z, true); }
    p.resetFall();
  }

  /** Player.rideTick: runs inside the player's tick, before the physics step. */
  function riderTick(): void {
    const b = riding();
    if (!b || !b.hasPlayer) { dismount(); return; }
    p.rideInput(input);
    input.forward = input.fwd;
    if (b.riders[0] === PLAYER) b.steer(input); else b.clearInput();
    // sneak always climbs out; the click that mounted gets 2 ticks of grace
    if (input.sneak && ++age > 2) { dismount(); return; }
    if (!input.sneak) age = Math.max(age, 3);
  }

  function mount(b: Boat): boolean {
    if (ride.current || b.removed || !mountPlayer(b)) return false;
    prevHook = p.rideTick;
    p.rideTick = riderTick;
    p.sitting = true;
    age = 0;
    syncRider(true);
    deps.message?.('Press Shift to dismount');
    return true;
  }

  function breakBoat(b: Boat, drop: boolean): void {
    if (riding() === b) dismount();
    if (drop) deps.drop(b.item, b.x, b.y + 0.3, b.z);
    sim.remove(b);
  }
  sim.events.onBreak = (b, drop) => breakBoat(b, drop);

  return {
    sim,
    riding,
    dismount,
    mount,
    isBoatItem: (id: number): boolean => boatWoodOf(id) >= 0,

    /** Right click with a boat item: set it on the water / block the ray meets. False when there is no room. */
    place(item: number, eye: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }): boolean {
      const wood = boatWoodOf(item);
      if (wood < 0) return false;
      const hit = deps.ray(BOAT_TARGETS);
      if (!hit) return false;
      const t = cellEntry([eye.x, eye.y, eye.z], [dir.x, dir.y, dir.z], [hit.x, hit.y, hit.z]);
      const x = eye.x + dir.x * t, z = eye.z + dir.z * t;
      let y = eye.y + dir.y * t;
      if (IS_WATERY[hit.id] === 1) y = hit.y + Math.max(waterHeight(deps.read, hit.x, hit.y, hit.z), 0.1);
      else if (hit.ny === 1) y = hit.y + 1;
      return sim.place(wood, x, y, z, p.yaw) !== null;
    },

    /** Right click on a boat: climb in (not while sneaking). True when the click was used. */
    use(eye: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }, limit: number, sneaking: boolean): boolean {
      if (sneaking || ride.current) return false;
      const h = sim.raycast(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, 4.5);
      if (!h || h.dist >= limit) return false;
      return mount(h.boat);
    },

    /** Left click: hurt the boat under the crosshair when nothing nearer is in the way. True when it was hit. */
    punch(eye: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }, limit: number): boolean {
      const h = sim.raycast(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, 4.5);
      if (!h || h.dist >= limit) return false;
      const creative = deps.creative();
      if (h.boat.hurt(deps.punchDamage(), creative)) breakBoat(h.boat, !creative);
      return true;
    },

    /** After the mobs' tick: step every boat, keep the rider seated; `stop` forces the rider out (death, travel). */
    tick(stop = false): void {
      sim.tick();
      const b = riding();
      if (ride.current && !b && (ride.current as Boat).kind === 'boat') { dismount(); return; }
      if (b && (stop || b.removed || !b.hasPlayer)) { dismount(); return; }
      if (b) syncRider();
    },

    serialize: (): number[][] => sim.serialize(),
    load(rows: unknown): void { if (riding()) dismount(); sim.load(rows); },
  };
}

export type Boats = ReturnType<typeof createBoats>;
export { BOAT_ITEMS };
